import type {
  GitHubInstallationSummary,
  GitHubRepositoryBranchSummary,
  GitHubRepositoryDirectorySummary,
  GitHubRepositorySummary,
  WorkspaceScope
} from '../../components/screens/RunScreens';
import { requestJson } from '../../components/workflowShared';
import {
  DEV_FULL_SKILLS,
  DEV_GITHUB_BRANCHES,
  DEV_GITHUB_TREE_DIRECTORIES,
  runGetDevGitHubRepositories,
  runGetIssues,
  runIsDevGitHubAccountEnabled,
  runRequestJson,
  type GitHubBranchesResponse,
  type GitHubRepositoriesResponse,
  type GitHubRepositoryTreeResponse,
  type RunDetailsResponse,
  type RunIssueViewModel,
  type RunRawIssue,
  type RunStatusAgent
} from './appRunData';

/** Everything needed to start a scan, stored with the pricing card so it survives reloads. */
export interface AppScanTarget {
  source: 'github';
  installationId: number;
  repositoryId: number;
  owner: string;
  repo: string;
  fullName: string;
  branch: string;
  scopePaths: string[];
  scanType: 'smart-contract' | 'web-api';
  assumptionsFilePath?: string;
  codebaseResourceId?: string;
}

interface AppUploadedScanTarget {
  source: 'upload';
  fileName: string;
  sizeBytes: number;
  codebaseResourceId: string;
}

export type AppPentestTarget = AppScanTarget | AppUploadedScanTarget;

interface AppUploadedCodebaseResource {
  id: string;
  workspaceId: string;
  kind: 'codebase';
  name: string;
  state: 'ready' | 'processing' | 'failed' | 'archived';
  source: 'upload';
}

export const isUploadedScanTarget = (target: AppPentestTarget): target is AppUploadedScanTarget => {
  return target.source === 'upload';
};

/** Picks the newest usable ZIP pointer from the workspace resource list. */
export const findReusableUploadedCodebase = (
  resources: Array<{
    id: string;
    workspaceId: string;
    kind: string;
    name: string;
    state: string;
    source?: string;
  }>,
  workspaceId: string
): { id: string; workspaceId: string; name: string } | undefined => {
  return resources.find((resource) => {
    return resource.workspaceId === workspaceId
      && resource.kind === 'codebase'
      && resource.source === 'upload'
      && resource.state === 'ready';
  });
};

export interface AppRepositoryChoice {
  installationId: number;
  repository: GitHubRepositorySummary;
}

const DEV_RUN_PREFIX = 'dev-run-';
const DEV_RUN_DURATION_MS = 60_000;
const DEV_SIMULATION_STARTED_MS = Date.now() - 20_000;
const DEV_RUN_MODELS = [
  { aiAgent: 'codex', model: 'gpt-daybreak-blue-latest' },
  { aiAgent: 'claude', model: 'claude-opus-5-5' }
];

const DEV_ISSUES: RunIssueViewModel[] = [
  {
    id: 'dev-1',
    title: 'Refund handler trusts client-supplied order total',
    summary: 'The refund endpoint reads `amount` from the request body instead of the stored order, so a caller can refund more than they paid.',
    detail: 'In `api/controllers/refunds.ts` the handler passes `req.body.amount` straight to the billing service.\n\nThe stored order total is never consulted.',
    impact: 'High. Any authenticated customer can withdraw arbitrary funds.',
    files: ['api/controllers/refunds.ts', 'api/services/billing/refund.ts'],
    sources: ['semgrep-code-security'],
    severity: 'high'
  },
  {
    id: 'dev-2',
    title: 'Webhook signature compared with non-constant-time equality',
    summary: 'GitHub webhook signatures are checked with `===`, which leaks timing information.',
    detail: 'Use `crypto.timingSafeEqual` on equal-length buffers.',
    impact: 'Medium. Signature forgery becomes feasible with enough requests.',
    files: ['api/services/github/webhooks.ts'],
    sources: ['trail-of-bits-static-analysis'],
    severity: 'medium'
  },
  {
    id: 'dev-3',
    title: 'Session cookie missing SameSite attribute',
    summary: 'The session cookie is issued without `SameSite`, relying on browser defaults.',
    detail: 'Set `SameSite=Lax` explicitly in `api/middleware/session.ts`.',
    impact: 'Low.',
    files: ['api/middleware/session.ts'],
    sources: ['forefy'],
    severity: 'low'
  }
];

const isDev = (): boolean => runIsDevGitHubAccountEnabled();

export const loadRepositoryChoices = async (
  sessionId: string,
  installations: GitHubInstallationSummary[]
): Promise<AppRepositoryChoice[]> => {
  if (isDev()) {
    const installationId = installations[0]?.id ?? 0;

    return runGetDevGitHubRepositories().map((repository) => ({ installationId, repository }));
  }

  const entries = await Promise.all(installations.map(async (installation) => {
    const response = await runRequestJson<GitHubRepositoriesResponse>(
      `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installation.id)}/repositories`
    );

    return (response.repositories ?? []).map((repository) => ({ installationId: installation.id, repository }));
  }));

  return entries.flat();
};

export const toScanTarget = (choice: AppRepositoryChoice, scope?: WorkspaceScope): AppScanTarget => ({
  source: 'github',
  installationId: choice.installationId,
  repositoryId: choice.repository.id,
  owner: choice.repository.owner,
  repo: choice.repository.repo,
  fullName: choice.repository.fullName,
  branch: scope?.branch || choice.repository.defaultBranch || 'main',
  scopePaths: scope?.scopePaths ?? [],
  scanType: scope?.scanType ?? 'smart-contract',
  assumptionsFilePath: scope?.assumptionsFilePath,
  codebaseResourceId: scope?.codebaseResourceId
});

export const uploadCodebaseZip = async (
  workspaceId: string,
  file: File,
  context: string
): Promise<AppUploadedCodebaseResource> => {
  if (isDev()) {
    return {
      id: `dev-upload-${Date.now()}`,
      workspaceId,
      kind: 'codebase',
      name: file.name,
      state: 'ready',
      source: 'upload'
    };
  }

  const formData = new FormData();

  formData.append('codebaseZip', file);
  formData.append('payload', JSON.stringify({ context }));

  const response = await requestJson<{ resource: AppUploadedCodebaseResource }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/resources/codebase-upload`,
    { method: 'POST', body: formData }
  );

  return response.resource;
};

/** What the backend reports about an uploaded ZIP; the counterpart of a repository, branch and folders. */
export interface AppUploadedCodebaseDetails {
  resourceId: string;
  name: string;
  fileName: string;
  archiveSizeBytes: number;
  uploadedAt: string;
  /** Pentest context given with the upload. */
  context?: string;
  /** Missing for older uploads that were never unpacked into the workspace. */
  contents?: {
    fileCount: number;
    directoryCount: number;
    totalBytes: number;
    directories: GitHubRepositoryDirectorySummary[];
  };
}

export const toUploadedScanTarget = (details: AppUploadedCodebaseDetails): AppUploadedScanTarget => ({
  source: 'upload',
  fileName: details.fileName,
  sizeBytes: details.archiveSizeBytes,
  codebaseResourceId: details.resourceId
});

export const loadUploadedCodebaseDetails = async (
  resource: { id: string; workspaceId: string; name: string },
  signal?: AbortSignal
): Promise<AppUploadedCodebaseDetails> => {
  if (isDev()) {
    return {
      resourceId: resource.id,
      name: resource.name,
      fileName: resource.name,
      archiveSizeBytes: 2_400_000,
      uploadedAt: new Date().toISOString(),
      contents: {
        fileCount: 412,
        directoryCount: DEV_GITHUB_TREE_DIRECTORIES.length,
        totalBytes: 9_800_000,
        directories: DEV_GITHUB_TREE_DIRECTORIES
      }
    };
  }

  const response = await runRequestJson<{ codebase: AppUploadedCodebaseDetails }>(
    `/workspaces/${encodeURIComponent(resource.workspaceId)}/resources/${encodeURIComponent(resource.id)}/codebase`,
    { signal }
  );

  return response.codebase;
};

const toDevRawIssues = (): RunRawIssue[] => DEV_ISSUES.map((issue, index) => ({
  issueId: index + 1,
  skill: issue.sources[0] ?? 'semgrep-code-security',
  relativePath: issue.files[0] ?? '',
  issue: {
    Title: issue.title,
    Summary: issue.summary,
    Detail: issue.detail,
    Impact: issue.impact,
    Files: issue.files,
    severity: issue.severity
  }
}));

/** Simulated run that finishes after a minute, so the progress UI can be tried in dev mock mode. */
const getDevRunDetails = (runId: string): RunDetailsResponse => {
  const startedMs = Number(runId.slice(DEV_RUN_PREFIX.length)) || DEV_SIMULATION_STARTED_MS;
  const elapsed = Math.min(1, (Date.now() - startedMs) / DEV_RUN_DURATION_MS);
  const modelCount = DEV_FULL_SKILLS.length * DEV_RUN_MODELS.length;
  const isScanDone = elapsed >= 0.85;
  const isReportDone = elapsed >= 1;
  const iso = (fraction: number) => new Date(startedMs + fraction * DEV_RUN_DURATION_MS).toISOString();
  const agents: RunStatusAgent[] = DEV_FULL_SKILLS.map((skill, skillIndex) => {
    const models = DEV_RUN_MODELS.map((entry, modelIndex) => {
      const finishesAt = 0.1 + 0.75 * ((skillIndex * DEV_RUN_MODELS.length + modelIndex + 1) / modelCount);
      const finished = elapsed >= finishesAt;

      return { ...entry, status: finished ? 'completed' : 'running', started: true, finished };
    });
    const finished = models.every((model) => model.finished);

    return { agent: skill, status: finished ? 'completed' : 'running', started: elapsed >= 0.08, finished, models };
  });

  return {
    status: {
      runId,
      target: 'dev',
      requestedSkills: DEV_FULL_SKILLS,
      requestedAiAgents: DEV_RUN_MODELS.map((entry) => entry.aiAgent),
      requestedAiAgentModels: DEV_RUN_MODELS,
      runDirectory: '',
      statusFilePath: '',
      status: isScanDone ? 'completed' : 'running',
      createdAt: iso(0),
      updatedAt: iso(elapsed),
      agents: elapsed >= 0.08 ? agents : [],
      deduplication: isScanDone
        ? { status: isReportDone ? 'completed' : 'running', finished: isReportDone }
        : undefined
    },
    issues: isReportDone ? { allIssues: toDevRawIssues() } : {}
  };
};

export const isDevRunId = (runId: string): boolean => isDev() && runId.startsWith('dev-');

export const loadRunDetails = async (runId: string): Promise<RunDetailsResponse> => {
  if (isDevRunId(runId)) {
    return getDevRunDetails(runId);
  }

  return runRequestJson<RunDetailsResponse>(`/getResults/${encodeURIComponent(runId)}`);
};

interface AppScanReport {
  issues: RunIssueViewModel[];
  /** Folders the scan covered; empty means the whole repository. */
  scopePaths: string[];
}

/** Findings of one finished scan and the folders it covered. Mock scans in dev mode report the sample issues. */
export const loadScanReport = async (scanId: string): Promise<AppScanReport> => {
  if (isDevRunId(scanId) && !scanId.startsWith(DEV_RUN_PREFIX)) {
    return { issues: DEV_ISSUES, scopePaths: [] };
  }

  const details = await loadRunDetails(scanId);
  const { scopePaths, scopePath } = details.status;

  return {
    issues: runGetIssues(details),
    scopePaths: scopePaths?.length ? scopePaths : scopePath ? [scopePath] : []
  };
};

const getRepositoryPath = (sessionId: string, installationId: number, repositoryId: number): string => {
  return `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installationId)}/repositories/${String(repositoryId)}`;
};

export const loadBranches = async (
  sessionId: string,
  installationId: number,
  repositoryId: number
): Promise<GitHubRepositoryBranchSummary[]> => {
  if (isDev()) {
    return DEV_GITHUB_BRANCHES;
  }

  const response = await runRequestJson<GitHubBranchesResponse>(
    `${getRepositoryPath(sessionId, installationId, repositoryId)}/branches`
  );

  return response.branches ?? [];
};

export const loadDirectories = async (
  sessionId: string,
  installationId: number,
  repositoryId: number,
  branch: string,
  signal?: AbortSignal
): Promise<GitHubRepositoryDirectorySummary[]> => {
  if (isDev()) {
    return DEV_GITHUB_TREE_DIRECTORIES;
  }

  const response = await runRequestJson<GitHubRepositoryTreeResponse>(
    `${getRepositoryPath(sessionId, installationId, repositoryId)}/tree?branch=${encodeURIComponent(branch)}`,
    { signal }
  );

  return response.directories ?? [];
};
